import { doc, onSnapshot, writeBatch, Timestamp, collection } from 'firebase/firestore';
import React, { useState, useEffect, useMemo, useRef } from 'react';

import { UserTypes } from '@shared/auth/roles';
import { MINIMUM_DATA_LENGTH } from '@shared/utils/constants';
import { BROKEN_PATIENT_REPORT_ACTIONS } from '@shared/utils/diagnostics.types.js';

import { useAuth } from './hooks';
import { db } from './firebase-config';
import createPatientData from './functions/create-patient-data';
import { UserDataContext } from './UserDataContext';
import { isPatient, reportBrokenPatientOnce } from './utils';

const { SUPPLIER, PHARMACIST, DOCTOR } = UserTypes;

/**  @param {import('firebase/auth').User} user  */
const recoverMissingPatientData = async (user) => {
  if (!user?.uid) {
    return;
  }

  const userId = user.uid;

  try {
    if (!user.email) {
      throw new Error('User email is required to recover patient data but is missing.');
    }

    // Attempt to create the patient data if it's missing
    const { error } = await createPatientData({ userId, userEmail: user.email });

    if (error) {
      throw new Error(error);
    }

    // If successful, log & report
    const batch = writeBatch(db);
    const now = Timestamp.now();

    batch.set(doc(collection(db, 'patients', userId, 'activity')), {
      createdAt: now,
      generalData: true,
      author: 'System',
      text: 'Auto-generated patient data at sign-in due to incomplete/missing details.',
      metadata: { action: 'PATIENT_INITIAL_DATA_AUTOMATICALLY_TRIGGERED', actionByUid: user.uid },
    });

    batch.set(
      doc(db, 'diagnostics', userId),
      {
        brokenPatientReport: {
          status: 'RESOLVED',
          resolvedAt: now,
          resolvedByUserType: 'patient',
          resolvedByUid: user.uid,
          action: BROKEN_PATIENT_REPORT_ACTIONS.AUTH_PATIENT_MISSING_FIRESTORE_DOC,
        },
      },
      { merge: true },
    );

    await batch.commit();
  } catch (error) {
    await reportBrokenPatientOnce(user.uid, BROKEN_PATIENT_REPORT_ACTIONS.AUTH_PATIENT_MISSING_FIRESTORE_DOC);
  }
};

/**
 * TECH DEBT: This is a hack to get the user data from the firestore
 * Someone thought it was smart to split user data across multiple collections 🤦🤦🤦
 * @param {UserType} userType
 * @returns {'orders' | 'doctors' | 'patients'} The collection name for the given user type
 */
const getUserCollectionName = (userType) => {
  switch (userType) {
    case SUPPLIER:
    case PHARMACIST:
      return 'orders';
    case DOCTOR:
      return 'doctors';
    default:
      return 'patients';
  }
};

export const UserDataProvider = ({ children }) => {
  const { user, userType } = useAuth();
  const [userData, setUserData] = useState(/** @type {UserData | null} */ (null));

  // prevents repeated reporting for the same login/session
  const hasReportedBrokenRef = useRef(false);

  useEffect(() => {
    if (!user?.uid || !userType) {
      return () => {};
    }

    hasReportedBrokenRef.current = false;

    const userCollectionName = getUserCollectionName(userType);

    const unsubscribe = onSnapshot(doc(db, userCollectionName, user.uid), async (docSnapshot) => {
      const data = docSnapshot.data() || {};

      setUserData({
        ...data,
        id: user.uid,
        dob: data?.dob,
        email: data?.email,
        lastName: data?.lastName,
        shipping: data?.shipping,
        firstName: data?.firstName,
        legalLastName: data?.legalLastName,
        legalFirstName: data?.legalFirstName,
        latestHealthSummaryReleaseId: data?.latestHealthSummaryReleaseId,
      });

      if (!isPatient(userType)) {
        return;
      }

      const isDataBroken = Object.keys(data).length < MINIMUM_DATA_LENGTH && !data?.isDoctor && !data?.isPharmacy;

      if (!isDataBroken) {
        return;
      }

      // Avoid spamming on snapshot refreshes
      if (hasReportedBrokenRef.current) {
        return;
      }

      hasReportedBrokenRef.current = true;

      if (!docSnapshot.exists()) {
        await recoverMissingPatientData(user);
        return;
      }

      await reportBrokenPatientOnce(user.uid, BROKEN_PATIENT_REPORT_ACTIONS.BROKEN_PATIENT_DATA_DETECTED);
    });

    return () => {
      unsubscribe();
    };
  }, [user?.uid, userType]);

  const value = useMemo(() => ({ userData, setUserData }), [userData, setUserData]);

  return <UserDataContext.Provider value={value}>{children}</UserDataContext.Provider>;
};
